import { PersonDTO } from '@iota/iota-management-portal-v4-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    DeletePerson,
    DeletePersonFailure,
    DeletePersonSuccess,
    LoadAllPersonsByCentralRegulator,
    LoadAllPersonsByCentralRegulatorFailure,
    LoadAllPersonsByCentralRegulatorSuccess,
    LoadPersonsByCompany,
    LoadPersonsFailure,
    LoadPersonsSuccess,
    PersonActionFailure,
    ReloadPerson,
    ReloadPersonSuccess,
    RemovePersonAddress,
    RemovePersonEmail,
    RemovePersonNote,
    RemovePersonPhoneNumber,
    UpsertPerson,
    UpsertPersonAddress,
    UpsertPersonEmail,
    UpsertPersonNote,
    UpsertPersonPhoneNumber,
    UpsertPersonSuccess
} from '../actions/person.actions';
import { AppState } from '../store';
import { CompanyMode } from '../../shared/constants-and-enums/company-mode.enum';

export interface PersonState extends EntityState<PersonDTO> {
    loading: boolean;
    actionLoading: boolean;
    error: any;
    loadedOffices: string[];
    loadedCompanies: string[];
}

function selectPersonId(a: PersonDTO): string {
    return a.personId;
}

function sortPersons(a: PersonDTO, b: PersonDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const personAdapter: EntityAdapter<PersonDTO> = createEntityAdapter<PersonDTO>({
    selectId: selectPersonId,
    sortComparer: sortPersons
});

export const initialState: PersonState = personAdapter.getInitialState({
    loading: true,
    actionLoading: false,
    error: undefined,
    loadedOffices: [],
    loadedCompanies: []
});

export const personReducer = createReducer(
    initialState,

    on(LoadPersonsByCompany, (state) => ({
        ...state,
        loading: true
    })),
    on(LoadPersonsSuccess, (state, { persons, officeId, companyId }) =>
        personAdapter.upsertMany(persons || [], {
            ...state,
            loading: false,
            loadedOffices: state.loadedOffices.concat(officeId ? [officeId] : []),
            loadedCompanies: state.loadedCompanies.concat(companyId ? [companyId] : [])
        })
    ),
    on(LoadPersonsFailure, (state, { error }) => ({ ...state, error, loading: false })),

    on(DeletePerson, (state) => ({ ...state, actionLoading: true })),
    on(DeletePersonSuccess, (state, { personId }) => personAdapter.removeOne(personId, { ...state, actionLoading: false })),
    on(DeletePersonFailure, (state, { error }) => ({ ...state, error, actionLoading: false })),

    on(PersonActionFailure, (state, { error }) => ({ ...state, actionLoading: false, error })),

    on(ReloadPersonSuccess, (state, { person }) => personAdapter.upsertOne(person, { ...state, actionLoading: false })),

    on(
        UpsertPerson,
        ReloadPerson,
        UpsertPersonEmail,
        RemovePersonEmail,
        UpsertPersonPhoneNumber,
        RemovePersonPhoneNumber,
        UpsertPersonAddress,
        RemovePersonAddress,
        UpsertPersonNote,
        RemovePersonNote,
        (state) => ({
            ...state,
            actionLoading: true
        })
    ),

    on(UpsertPersonSuccess, (state, { person }) => personAdapter.upsertOne(person, { ...state, actionLoading: false })),
    on(LoadAllPersonsByCentralRegulator, (state) => ({
        ...state,
        loading: true
    })),
    on(LoadAllPersonsByCentralRegulatorSuccess, (state, { persons }) =>
        personAdapter.upsertMany(persons || [], {
            ...state,
            loading: false
        })
    ),
    on(LoadAllPersonsByCentralRegulatorFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error
    }))
);

const { selectAll } = personAdapter.getSelectors();

export const personState = (state: AppState) => state.personState;
export const persons = createSelector(personState, selectAll);
export const personById = (personId: string) => createSelector(persons, (allPersons) => allPersons.find((p) => p.personId === personId));
export const personsLoading = createSelector(personState, (state) => state.loading);
export const personActionLoading = createSelector(personState, (state) => state.actionLoading);

export const personsBySelectedBusinessPartner = (businessPartnerId: string, mode: CompanyMode) =>
    createSelector(personState, (state: PersonState) => {
        if (mode !== CompanyMode.REGULATOR) {
            return selectAll(state).filter((person) => person.companyId === businessPartnerId);
        }
        return selectAll(state).filter((person) => person.centralRegulatorId === businessPartnerId);
    });
